import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import EditableDescription from './EditableDescription';
import ImportSchemaModal from './ImportSchemaModal';
import SchemaEditorModal from './SchemaEditorModal';
import { ErdViewModal } from '../ErdViewInit/ErdViewModal';
import '../shared.css';
import './SchemaViewer.css';

// Module-level cache: key = `${kgId}__${versionId}`, value = raw schema response
// Schema for a given version never changes, so we can cache indefinitely per session.
const schemaCache = new Map<string, any>();

interface SchemaViewerProps {
  kgId: string;
  versionId: string;
}

interface PropertyInfo {
  name: string;
  type: string;
  description?: string;
}

interface NodeInfo {
  label: string;
  description?: string;
  properties: PropertyInfo[];
}

interface EdgeInfo {
  type: string;
  description?: string;
  properties: PropertyInfo[];
}

const SchemaViewer: React.FC<SchemaViewerProps> = ({ kgId, versionId }) => {
  const [nodes, setNodes] = useState<NodeInfo[]>([]);
  const [edges, setEdges] = useState<EdgeInfo[]>([]);
  const [loading, setLoading] = useState(() => !schemaCache.has(`${kgId}__${versionId}`));
  const [refreshing, setRefreshing] = useState(false);
  const [lastLoaded, setLastLoaded] = useState<Date | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState<'nodes' | 'edges'>('nodes');
  const [saving, setSaving] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showSchemaEditor, setShowSchemaEditor] = useState(false);
  const [showERDEditorModal, setShowERDEditorModal] = useState(false);

  const [customAttributes, setCustomAttributes] = useState<any>({});
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [expandedEdges, setExpandedEdges] = useState<Set<string>>(new Set());
  const [nodeCounts, setNodeCounts] = useState<{ [label: string]: number }>({});
  const [edgeCounts, setEdgeCounts] = useState<{ [type: string]: number }>({});
  const [totalNodeCount, setTotalNodeCount] = useState<number>(0);
  const [totalEdgeCount, setTotalEdgeCount] = useState<number>(0);
  const [nodesMissingName, setNodesMissingName] = useState<string[]>([]);

  // Prevent stale async responses from overwriting newer ones
  const loadSeqRef = useRef(0);

  useEffect(() => {
    loadSchemaData(false);
  }, [kgId, versionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Parse helpers ───────────────────────────────────────────────────────────

  function applySchemaResponse(schemaResponse: any, customAttrsResponse: any) {
    const schemaData =
      schemaResponse.data?.attributes?.schema ||
      schemaResponse.data?.schema ||
      schemaResponse.data || {};

    const customAttrs = customAttrsResponse.data?.attributes?.custom_attributes || {};
    setCustomAttributes(customAttrs);

    const nodeDescriptions = customAttrs.node_descriptions || {};
    const edgeDescriptions = customAttrs.edge_descriptions || {};

    const nodesList: NodeInfo[] = [];
    const missingNameNodes: string[] = [];

    let nodeLabels: string[] = [];
    if (schemaData.nodes) {
      nodeLabels = Object.keys(schemaData.nodes);
    } else if (typeof schemaData === 'object') {
      nodeLabels = Object.keys(schemaData).filter(key => {
        const value = schemaData[key];
        return typeof value === 'object' && value !== null;
      });
    }

    nodeLabels.forEach(label => {
      const nodeData = schemaData.nodes ? schemaData.nodes[label] : schemaData[label];
      const nodeDesc = nodeDescriptions[label] || {};
      const properties: PropertyInfo[] = [];
      let hasNameProperty = false;

      if (nodeData && typeof nodeData === 'object') {
        const propsObj = nodeData.properties || nodeData;
        Object.keys(propsObj).forEach(propName => {
          const propData = propsObj[propName];
          if (typeof propData === 'object' && propData.type) {
            properties.push({
              name: propName,
              type: propData.type || 'STRING',
              description: nodeDesc[propName] || '',
            });
            if (propName === 'name') hasNameProperty = true;
          }
        });
      }
      if (!hasNameProperty) missingNameNodes.push(label);
      nodesList.push({ label, description: nodeDesc.__description || '', properties });
    });

    setNodesMissingName(missingNameNodes);

    const edgesList: EdgeInfo[] = [];
    if (schemaData.edges || schemaData.relationships) {
      const edgesData = schemaData.edges || schemaData.relationships;
      Object.keys(edgesData).forEach(edgeType => {
        const edgeData = edgesData[edgeType];
        const edgeDesc = edgeDescriptions[edgeType] || {};
        const properties: PropertyInfo[] = [];
        if (edgeData && typeof edgeData === 'object') {
          const propsObj = edgeData.properties || edgeData;
          Object.keys(propsObj).forEach(propName => {
            const propData = propsObj[propName];
            if (typeof propData === 'object' && propData.type) {
              properties.push({
                name: propName,
                type: propData.type || 'STRING',
                description: edgeDesc[propName] || '',
              });
            }
          });
        }
        edgesList.push({ type: edgeType, description: edgeDesc.__description || '', properties });
      });
    }

    setNodes(nodesList);
    setEdges(edgesList);
  }

  function applySummaryResponse(summaryResponse: any) {
    const summaryData =
      summaryResponse.data?.attributes?.summary ||
      summaryResponse.data?.summary ||
      summaryResponse.data?.attributes ||
      summaryResponse.data || {};

    const nodeCountsMap: { [label: string]: number } = {};
    let totalNodes = 0;
    if (summaryData.nodes) {
      Object.keys(summaryData.nodes).forEach(label => {
        const d = summaryData.nodes[label];
        const count = typeof d === 'number' ? d : (d?.count ?? d?.node_count ?? 0);
        nodeCountsMap[label] = count;
        totalNodes += count;
      });
    } else if (summaryData.node_counts) {
      Object.keys(summaryData.node_counts).forEach(label => {
        const count = summaryData.node_counts[label];
        nodeCountsMap[label] = typeof count === 'number' ? count : 0;
        totalNodes += nodeCountsMap[label];
      });
    }

    const edgeCountsMap: { [type: string]: number } = {};
    let totalEdges = 0;
    const edgesData = summaryData.edges || summaryData.relationships;
    if (edgesData) {
      Object.keys(edgesData).forEach(type => {
        const d = edgesData[type];
        const count = typeof d === 'number' ? d : (d?.count ?? d?.edge_count ?? 0);
        edgeCountsMap[type] = count;
        totalEdges += count;
      });
    } else {
      const ecData = summaryData.edge_counts || summaryData.relationship_counts;
      if (ecData) {
        Object.keys(ecData).forEach(type => {
          const count = ecData[type];
          edgeCountsMap[type] = typeof count === 'number' ? count : 0;
          totalEdges += edgeCountsMap[type];
        });
      }
    }

    setNodeCounts(nodeCountsMap);
    setEdgeCounts(edgeCountsMap);
    setTotalNodeCount(totalNodes);
    setTotalEdgeCount(totalEdges);
  }

  // ── Main load ───────────────────────────────────────────────────────────────

  const loadSchemaData = async (forceRefresh: boolean) => {
    const seq = ++loadSeqRef.current;
    const cacheKey = `${kgId}__${versionId}`;
    const cached = schemaCache.get(cacheKey);

    // On first load: show skeleton until we have something to display.
    // On manual refresh: keep existing content, show refreshing spinner.
    if (!cached) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError('');

    try {
      // Schema: serve from cache unless force-refreshing
      let schemaResponse: any;
      if (!forceRefresh && cached) {
        schemaResponse = cached;
      } else {
        schemaResponse = await apiService.getSchema(kgId, versionId);
        if (seq !== loadSeqRef.current) return; // stale
        schemaCache.set(cacheKey, schemaResponse);
      }

      // Custom attrs: always fresh (descriptions can change)
      const customAttrsResponse: any = await apiService.getCustomAttributes(kgId, versionId);
      if (seq !== loadSeqRef.current) return;

      applySchemaResponse(schemaResponse, customAttrsResponse);

      // Summary: non-fatal, always fresh (counts change as data is ingested)
      try {
        const summaryResponse: any = await apiService.getSummary(kgId, versionId, false);
        if (seq !== loadSeqRef.current) return;
        applySummaryResponse(summaryResponse);
      } catch {
        // Non-fatal — counts are nice-to-have
      }

      setLastLoaded(new Date());
    } catch (err: any) {
      if (seq !== loadSeqRef.current) return;
      setError(extractErrorMessage(err, 'Failed to load schema'));
    } finally {
      if (seq === loadSeqRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  const handleUpdateNodeDescription = async (nodeLabel: string, description: string) => {
    await updateDescription('node', nodeLabel, '__description', description);
  };

  const handleUpdateNodePropertyDescription = async (nodeLabel: string, propertyName: string, description: string) => {
    await updateDescription('node', nodeLabel, propertyName, description);
  };

  const handleUpdateEdgeDescription = async (edgeType: string, description: string) => {
    await updateDescription('edge', edgeType, '__description', description);
  };

  const handleUpdateEdgePropertyDescription = async (edgeType: string, propertyName: string, description: string) => {
    await updateDescription('edge', edgeType, propertyName, description);
  };

  const toggleNodeExpand = (nodeLabel: string) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeLabel)) {
        newSet.delete(nodeLabel);
      } else {
        newSet.add(nodeLabel);
      }
      return newSet;
    });
  };

  const toggleEdgeExpand = (edgeType: string) => {
    setExpandedEdges(prev => {
      const newSet = new Set(prev);
      if (newSet.has(edgeType)) {
        newSet.delete(edgeType);
      } else {
        newSet.add(edgeType);
      }
      return newSet;
    });
  };

  const expandAllNodes = () => {
    setExpandedNodes(new Set(nodes.map(n => n.label)));
  };

  const collapseAllNodes = () => {
    setExpandedNodes(new Set());
  };

  const expandAllEdges = () => {
    setExpandedEdges(new Set(edges.map(e => e.type)));
  };

  const collapseAllEdges = () => {
    setExpandedEdges(new Set());
  };

  const handleImportDescriptions = async (importedDescriptions: any) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Fetch latest custom attributes to merge with imported descriptions
      const response: any = await apiService.getCustomAttributes(kgId, versionId);
      const latestCustomAttrs = response.data?.attributes?.custom_attributes || {};

      // Create deep copy to avoid mutation
      const updatedAttrs = JSON.parse(JSON.stringify(latestCustomAttrs));

      // Initialize structures if they don't exist
      if (!updatedAttrs.node_descriptions) {
        updatedAttrs.node_descriptions = {};
      }
      if (!updatedAttrs.edge_descriptions) {
        updatedAttrs.edge_descriptions = {};
      }

      // Merge imported node descriptions
      Object.keys(importedDescriptions.node_descriptions).forEach(label => {
        if (!updatedAttrs.node_descriptions[label]) {
          updatedAttrs.node_descriptions[label] = {};
        }
        Object.keys(importedDescriptions.node_descriptions[label]).forEach(field => {
          updatedAttrs.node_descriptions[label][field] = importedDescriptions.node_descriptions[label][field];
        });
      });

      // Merge imported edge descriptions
      Object.keys(importedDescriptions.edge_descriptions).forEach(type => {
        if (!updatedAttrs.edge_descriptions[type]) {
          updatedAttrs.edge_descriptions[type] = {};
        }
        Object.keys(importedDescriptions.edge_descriptions[type]).forEach(field => {
          updatedAttrs.edge_descriptions[type][field] = importedDescriptions.edge_descriptions[type][field];
        });
      });

      // Save updated attributes
      await apiService.updateCustomAttributes(kgId, versionId, updatedAttrs);

      // Reload schema data to reflect changes
      await loadSchemaData(false);

      setSuccess('Descriptions imported successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to import descriptions'));
    } finally {
      setSaving(false);
    }
  };

  const updateDescription = async (type: 'node' | 'edge', entityName: string, propertyName: string, description: string) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // Fetch latest custom attributes to ensure we have fresh data
      const response: any = await apiService.getCustomAttributes(kgId, versionId);
      const latestCustomAttrs = response.data?.attributes?.custom_attributes || {};

      // Create deep copy to avoid mutation
      const updatedAttrs = JSON.parse(JSON.stringify(latestCustomAttrs));

      // Initialize structures if they don't exist
      if (type === 'node') {
        if (!updatedAttrs.node_descriptions) {
          updatedAttrs.node_descriptions = {};
        }
        if (!updatedAttrs.node_descriptions[entityName]) {
          updatedAttrs.node_descriptions[entityName] = {};
        }
        updatedAttrs.node_descriptions[entityName][propertyName] = description;
      } else {
        if (!updatedAttrs.edge_descriptions) {
          updatedAttrs.edge_descriptions = {};
        }
        if (!updatedAttrs.edge_descriptions[entityName]) {
          updatedAttrs.edge_descriptions[entityName] = {};
        }
        updatedAttrs.edge_descriptions[entityName][propertyName] = description;
      }

      // Save updated attributes
      await apiService.updateCustomAttributes(kgId, versionId, updatedAttrs);

      // Update local state
      setCustomAttributes(updatedAttrs);

      // Update the local nodes/edges state directly without full reload
      if (type === 'node') {
        setNodes(prevNodes =>
          prevNodes.map(node => {
            if (node.label === entityName) {
              if (propertyName === '__description') {
                return { ...node, description };
              } else {
                return {
                  ...node,
                  properties: node.properties.map(prop =>
                    prop.name === propertyName ? { ...prop, description } : prop
                  )
                };
              }
            }
            return node;
          })
        );
      } else {
        setEdges(prevEdges =>
          prevEdges.map(edge => {
            if (edge.type === entityName) {
              if (propertyName === '__description') {
                return { ...edge, description };
              } else {
                return {
                  ...edge,
                  properties: edge.properties.map(prop =>
                    prop.name === propertyName ? { ...prop, description } : prop
                  )
                };
              }
            }
            return edge;
          })
        );
      }

      setSuccess('Description updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to update description'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="schema-viewer tab-root">
        <div className="tab-loader">
          <span className="tab-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="schema-viewer tab-root">
      {/* Topbar */}
      <div className="tab-topbar">
        <div className="tab-topbar-left">
          <div className="inner-tabs" style={{ border: 'none', marginBottom: 0 }}>
            <button
              className={`inner-tab${activeTab === 'nodes' ? ' inner-tab--active' : ''}`}
              onClick={() => setActiveTab('nodes')}
            >
              Nodes <span style={{ opacity: 0.6, fontSize: 11 }}>({nodes.length})</span>
            </button>
            <button
              className={`inner-tab${activeTab === 'edges' ? ' inner-tab--active' : ''}`}
              onClick={() => setActiveTab('edges')}
            >
              Edges <span style={{ opacity: 0.6, fontSize: 11 }}>({edges.length})</span>
            </button>
          </div>
          <span className="tab-topbar-sep" />
          {activeTab === 'nodes' && (
            <>
              <button className="tab-btn tab-btn--ghost" onClick={expandAllNodes}>Expand All</button>
              <button className="tab-btn tab-btn--ghost" onClick={collapseAllNodes}>Collapse All</button>
            </>
          )}
          {activeTab === 'edges' && (
            <>
              <button className="tab-btn tab-btn--ghost" onClick={expandAllEdges}>Expand All</button>
              <button className="tab-btn tab-btn--ghost" onClick={collapseAllEdges}>Collapse All</button>
            </>
          )}
        </div>
        <div className="tab-topbar-right">
          {lastLoaded && (
            <span className="tab-stat-pill">
              {refreshing ? 'Refreshing…' : `Loaded ${lastLoaded.toLocaleTimeString()}`}
            </span>
          )}
          <button
            className="tab-btn tab-btn--ghost"
            onClick={() => loadSchemaData(true)}
            disabled={refreshing || loading}
            title="Re-fetch schema from server"
          >
            {refreshing ? '↻ …' : '↻ Refresh'}
          </button>
          <span className="tab-topbar-sep" />
          <button className="tab-btn tab-btn--ghost" onClick={() => setShowERDEditorModal(true)}>ERD Editor</button>
          <button className="tab-btn tab-btn--ghost" onClick={() => setShowSchemaEditor(true)}>Schema Editor</button>
          <button className="tab-btn tab-btn--ghost" onClick={() => setShowImportModal(true)}>Import Descriptions</button>
        </div>
      </div>

      {error && (
        <div className="tab-banner tab-banner--error">
          <span className="tab-banner-icon">⚠</span>
          <span className="tab-banner-body">{error}</span>
        </div>
      )}
      {success && (
        <div className="tab-banner tab-banner--success">
          <span className="tab-banner-body">{success}</span>
        </div>
      )}
      {saving && (
        <div className="tab-banner tab-banner--warning">
          <span className="tab-banner-body">Saving…</span>
        </div>
      )}

      {nodesMissingName.length > 0 && (
        <div className="tab-banner tab-banner--warning">
          <span className="tab-banner-icon">⚠</span>
          <span className="tab-banner-body">
            <strong>Required property "name" missing in:</strong>{' '}
            {nodesMissingName.join(', ')}
          </span>
        </div>
      )}

      {activeTab === 'nodes' && (
        <div className="schema-content">
          {loading ? (
            <div className="sv-skeleton-list">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="sv-skeleton-group" style={{ animationDelay: `${i * 0.06}s` }}>
                  <div className="sv-skeleton-head" />
                </div>
              ))}
            </div>
          ) : nodes.length === 0 ? (
            <div className="tab-empty">
              <div className="tab-empty-icon">⬡</div>
              <div className="tab-empty-title">No nodes in schema</div>
            </div>
          ) : (
            <>
              {nodes.map((node) => {
                const isExpanded = expandedNodes.has(node.label);
                return (
                  <div key={node.label} className="schema-group">
                    <div className="schema-group-header">
                      <div className="header-content">
                        <h3>
                          <button
                            className="expand-toggle"
                            onClick={() => toggleNodeExpand(node.label)}
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            {isExpanded ? '▼' : '▶'}
                          </button>
                          <span className="node-badge">NODE</span>
                          {node.label}
                          <span className="property-count">({node.properties.length} properties)</span>
                          {nodeCounts[node.label] !== undefined && (
                            <span className="entity-count">
                              {nodeCounts[node.label].toLocaleString()} nodes
                            </span>
                          )}
                        </h3>
                        <div className="entity-description-container">
                          <EditableDescription
                            value={node.description || ''}
                            onSave={(desc) => handleUpdateNodeDescription(node.label, desc)}
                            placeholder="Click to add node description"
                            disabled={saving}
                          />
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      node.properties.length > 0 ? (
                        <div className="schema-table-container">
                          <table className="schema-table">
                            <thead>
                              <tr>
                                <th>Property</th>
                                <th>Type</th>
                                <th>Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {node.properties.map((prop) => (
                                <tr key={prop.name}>
                                  <td className="property-name">{prop.name}</td>
                                  <td>
                                    <span className={`type-badge type-${prop.type.toLowerCase()}`}>
                                      {prop.type}
                                    </span>
                                  </td>
                                  <td className="property-description">
                                    <EditableDescription
                                      value={prop.description || ''}
                                      onSave={(desc) => handleUpdateNodePropertyDescription(node.label, prop.name, desc)}
                                      placeholder="Click to add property description"
                                      disabled={saving}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="no-properties">No properties defined</div>
                      )
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {activeTab === 'edges' && (
        <div className="schema-content">
          {loading ? (
            <div className="sv-skeleton-list">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="sv-skeleton-group" style={{ animationDelay: `${i * 0.06}s` }}>
                  <div className="sv-skeleton-head" />
                </div>
              ))}
            </div>
          ) : edges.length === 0 ? (
            <div className="tab-empty">
              <div className="tab-empty-icon">⬡</div>
              <div className="tab-empty-title">No edges in schema</div>
            </div>
          ) : (
            <>
              {edges.map((edge) => {
                const isExpanded = expandedEdges.has(edge.type);
                return (
                  <div key={edge.type} className="schema-group">
                    <div className="schema-group-header">
                      <div className="header-content">
                        <h3>
                          <button
                            className="expand-toggle"
                            onClick={() => toggleEdgeExpand(edge.type)}
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            {isExpanded ? '▼' : '▶'}
                          </button>
                          <span className="edge-badge">EDGE</span>
                          {edge.type}
                          <span className="property-count">({edge.properties.length} properties)</span>
                          {edgeCounts[edge.type] !== undefined && (
                            <span className="entity-count">
                              {edgeCounts[edge.type].toLocaleString()} edges
                            </span>
                          )}
                        </h3>
                        <div className="entity-description-container">
                          <EditableDescription
                            value={edge.description || ''}
                            onSave={(desc) => handleUpdateEdgeDescription(edge.type, desc)}
                            placeholder="Click to add edge description"
                            disabled={saving}
                          />
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      edge.properties.length > 0 ? (
                        <div className="schema-table-container">
                          <table className="schema-table">
                            <thead>
                              <tr>
                                <th>Property</th>
                                <th>Type</th>
                                <th>Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {edge.properties.map((prop) => (
                                <tr key={prop.name}>
                                  <td className="property-name">{prop.name}</td>
                                  <td>
                                    <span className={`type-badge type-${prop.type.toLowerCase()}`}>
                                      {prop.type}
                                    </span>
                                  </td>
                                  <td className="property-description">
                                    <EditableDescription
                                      value={prop.description || ''}
                                      onSave={(desc) => handleUpdateEdgePropertyDescription(edge.type, prop.name, desc)}
                                      placeholder="Click to add property description"
                                      disabled={saving}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="no-properties">No properties defined</div>
                      )
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {showImportModal && (
        <ImportSchemaModal
          kgId={kgId}
          versionId={versionId}
          currentNodes={nodes}
          currentEdges={edges}
          onClose={() => setShowImportModal(false)}
          onImport={handleImportDescriptions}
        />
      )}

      {showSchemaEditor && (
        <SchemaEditorModal
          kgId={kgId}
          versionId={versionId}
          nodes={nodes}
          edges={edges}
          customAttributes={customAttributes}
          onClose={() => setShowSchemaEditor(false)}
          onSave={() => { schemaCache.delete(`${kgId}__${versionId}`); loadSchemaData(false); }}
        />
      )}

      {showERDEditorModal && (
        <ErdViewModal kg_id={kgId} version_id={versionId} on_close={() => setShowERDEditorModal(false)}/>
      )}
    </div>
  );
};

export default SchemaViewer;
